import { CardConfig } from '@affinis/smartus-components/lib/card/card.component.types';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Inject,
  Injector,
  Input,
} from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';

import { IncidentClient, SmartPortalProcessError, UploadFile } from '@core/api';
import { FormSubmitService } from '@core/services/form-submit-service/form-submit.service';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { saveAs } from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-ticket-create-base',
  templateUrl: './ticket-create.base.html',
  styleUrls: ['./ticket-create.base.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TicketCreateBaseComponent {
  private static readonly errorKeyByProcessError = new Map<
    SmartPortalProcessError,
    string
  >([
    [SmartPortalProcessError.INCIDENT_ALREADY_EXISTS, 'duplicateIncident'],
    [SmartPortalProcessError.INVALID_INCIDENT_CLASS, 'invalidIncidentClass'],
    [SmartPortalProcessError.FILE_CONTENT_TYPE, 'invalidFileType'],
    [SmartPortalProcessError.INVALID_DOCUMENT_TYPE, 'invalidDocumentType'],
    [SmartPortalProcessError.FILE_NAME, 'invalidFileName'],
    [SmartPortalProcessError.FILE_UPLOAD_SIZE, 'fileLimit'],
    [SmartPortalProcessError.FILE_UPLOAD_TOTAL_SIZE, 'fileLimit'],
    [SmartPortalProcessError.FILE_UPLOAD_COUNT, 'fileLimit'],
  ]);

  @Input() navigation = true;

  @Input() id;

  loading = false;

  loadingMessage = '';

  card: CardConfig;

  dsgvoUrl: string;

  form = new FormGroup({});

  formModel = {};

  formFields: FormlyFieldConfig[];

  filledForm: FormlyFieldConfig[] | null;

  extraConfirmFormFields?: FormlyFieldConfig[];

  showExtraConfirmFormFields: boolean;

  title: string;

  description: string;

  descriptionStyle: string;

  descriptionContainerStyle: string;

  subDescription: string;

  subDescriptionStyle: string;

  subDescriptionContainerStyle: string;

  showTicketCreateData: boolean;

  patchValue: Observable<any>;

  options: FormlyFormOptions = {
    formState: {
      mainModel: this.formModel,
    },
  };

  successTitle: string;

  successMessage: string;

  showPdf = false;

  blobfilename = '';

  navigationUrl = '/tickets_dtag';

  redirectButtonLabel: string = '';

  protected prepareSubmit: (data) => any;

  protected clientEndpoint: (data) => Observable<any>;

  protected router: Router;

  public cd: ChangeDetectorRef;

  protected el: ElementRef;

  private dialog: MatDialog;

  protected translateService: TranslateService;

  protected isSubmitted: boolean;

  protected formSubmitService: FormSubmitService;

  protected msalService: MsalService;

  private readonly toastrService: ToastrService;

  protected isContactForm: boolean = false;

  protected incidentClient = inject(IncidentClient);

  cardConfirm: CardConfig;

  constructor(@Inject(Injector) injector: Injector) {
    this.router = injector.get(Router);
    this.cd = injector.get(ChangeDetectorRef);
    this.el = injector.get(ElementRef);
    this.dialog = injector.get(MatDialog);
    this.toastrService = injector.get(ToastrService);

    this.translateService = injector.get(TranslateService);

    this.formSubmitService = injector.get(FormSubmitService);

    this.formSubmitService.isSubmitted.subscribe(res => {
      this.isSubmitted = res;
    });

    this.msalService = injector.get(MsalService);

    this.successTitle = this.translateService.instant(
      'ticket.create.success.title'
    );

    this.successMessage = this.translateService.instant(
      'ticket.create.success.message'
    );

    this.loadingMessage = this.translateService.instant('form.loading_message');

    this.redirectButtonLabel = this.translateService.instant(
      'ticket.create.success.redirect_to_my_tickets'
    );
  }

  validateForm() {
    if (
      (this.form.value as any)?.searchLocation?.searchResult?.id === undefined
    ) {
      (this.form.get('searchLocation.searchResult') as any)?.setValue(
        undefined,
        { emitEvent: false }
      );
    }

    for (const control in this.form.controls) {
      if (this.form.controls[control].invalid) {
        if (control === 'areaRequest') {
          this.toastrService.error(
            this.translateService.instant('ticket.create.area_request_required')
          );
        }
      }
    }

    if (this.form.valid) {
      this.showFormSummary();
    } else {
      this.scrollToFirstInvalidControl(true);
    }
  }

  protected async scrollToFirstInvalidControl(repeatScroll: boolean) {
    this.form.markAllAsTouched();
    const firstInvalidControl: HTMLElement =
      this.el.nativeElement.querySelector('form .ng-invalid');

    try {
      if (firstInvalidControl) {
        firstInvalidControl.scrollIntoView({ behavior: 'smooth' });
        firstInvalidControl.focus();
      }
    } catch (e) {
      console.error('[ticket-create] scrollToFirstInvalidControl:', e);
    } finally {
      if (repeatScroll) {
        setTimeout(() => {
          this.scrollToFirstInvalidControl(false);
        }, 10);
      }
    }
  }

  async showFormSummary() {
    this.filledForm = this.formFields;
    this.cd.detectChanges();
  }

  downloadAttachment(file: File) {
    if (!file) {
      return;
    }
    saveAs(file, file.name);
  }

  fileRead(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => {
        resolve(fr.result as string);
      };
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });
  }

  discardSumUpForm() {
    this.filledForm = null;
    this.setFocusOnFirstField();
  }

  async submit() {
    this.loading = true;

    const files = this.form.get('attachments') as unknown as FormArray;

    const packedFiles: Array<UploadFile> = [];
    if (files !== null) {
      for await (const [_index, f] of files.controls.entries()) {
        const cont = (await this.fileRead(f.value.attachment)).split(',')[1];
        packedFiles.push({
          contentBase64Encoded: cont,
          metadata: {
            isInternal: false,
            documentId: '',
            name: f.value.attachment.name,
            guidExternalSystem: undefined,
            crmGuidOriginRecord: undefined,
            filename: f.value.attachment.name,
            dynamicsUri: undefined,
            publishDocument: undefined,
            statusCode: 'Aktiv',
            contactId: undefined,
            rentalContractId: undefined,
            functionalLocationId: undefined,
            accountId: undefined,
            documentTypeId: f.value.attachmentType,
            changeDate: undefined,
          },
        });
      }
    }

    const preparedData = this.prepareSubmit(this.form.value);

    const parts = this.router.url.split('/');
    const lastPart = parts[parts.length - 1];
    const isContactForm = lastPart.split('?')[0] === 'contact';

    this.clientEndpoint({
      ...preparedData,
      files: packedFiles.length > 0 ? packedFiles : null,
    }).subscribe({
      next: res => {
        console.log('client endpoint next');
        this.loading = false;

        if (res?.blobfilename) {
          this.showPdf = true;
          this.blobfilename = res.blobfilename;
        } else if (res?.ticketNumber) {
          const dialogSuccessMessage = isContactForm
            ? this.successMessage
            : `${this.successMessage}${this.translateService.instant(
                'ticket.create.success.message_ticketNumber_addition'
              )}${res.ticketNumber}`;

          this.openSuccessDialog(dialogSuccessMessage);
        } else {
          this.openSuccessDialog(this.successMessage);
        }

        this.cd.detectChanges();
      },
      error: error => {
        this.handleError(error);
        this.formSubmitService.isSubmitted.emit(false);
        this.loading = false;
        this.cd.detectChanges();
      },
    });
  }

  handleError(error: any) {
    let errTitle = this.translateService.instant('ticket.create.error.title');
    let errMessage = this.translateService.instant(
      'ticket.create.error.message'
    );

    const errorKey = TicketCreateBaseComponent.errorKeyByProcessError.get(
      error.errorCode
    );

    if (errorKey) {
      errTitle = this.translateService.instant(
        `ticket.create.error.${errorKey}.title`
      );
      errMessage = this.translateService.instant(
        `ticket.create.error.${errorKey}.message`
      );
    }
    this.loading = false;
    this.openErrorDialog(errTitle, errMessage);
    console.error(error);
  }

  getStatusPath() {
    const parts = this.router.url.split('/'); //  ['', 'ticket', 'any']
    const modulePath = parts[1];
    let newRoute = '';
    if (parts.length > 2) {
      newRoute = `/${modulePath}/status`;
    } else {
      newRoute = '/ticket/status';
    }
    return newRoute;
  }

  openSuccessDialog(message?: string) {
    const successDialog = this.dialog.open(DialogComponent, {
      panelClass: 'smartportal-dialog',
      width: '500px',
      data: {
        type: 'okButton',
        title: this.successTitle,
        message: message ?? this.successMessage,
        okButtonText: this.translateService.instant(
          'ticket.create.success.button__text'
        ),
        isContactForm: this.isContactForm,
        navigationUrl: this.navigationUrl,
        redirectButtonLabel: this.redirectButtonLabel,
      },
    });

    successDialog.afterClosed().subscribe({
      next: () => {
        const targetUrl = this.getStatusPath();
        this.router.navigate([targetUrl]);
      },
    });
  }

  openErrorDialog(errorTitle: string, errorMessage: string) {
    this.dialog.open(DialogComponent, {
      width: '300px',
      data: {
        type: 'noButtons',
        title: errorTitle,
        message: errorMessage,
      },
    });
  }

  pressBackToEdit() {
    this.filledForm = null;
    this.setFocusOnFirstField();
  }

  setFocusOnFirstField() {
    setTimeout(() => {
      const firstField = this.findFirstFormField(this.formFields);
      if (firstField) {
        firstField.focus = true;
      }
    }, 0);
  }

  findFirstFormField(
    fieldConfig: FormlyFieldConfig[]
  ): FormlyFieldConfig | null {
    for (const field of fieldConfig) {
      if (field.fieldGroup && field.fieldGroup.length > 0) {
        const result = this.findFirstFormField(field.fieldGroup);
        if (result) {
          return result;
        }
      }

      if (field.props.disabled === false && field.type !== 'chapter') {
        return field;
      }
    }
    return null;
  }
}
